import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import ReactInputVerificationCode from "react-input-verification-code";
import { notification, Alert, Button, Input, Form } from "antd";
import firebase from "../FirebaseConfig.js";
import firebaseApp from "firebase/app";
import { firebaseIdToken } from "../util/firebaseIdToken";
import { useHistory } from "react-router-dom";
import config from "../Configurations.js";
import { Link } from "react-router-dom";
import styles from "./Signup/signup.module.scss";
import loginStyles from "./Login/login.module.scss";
import LeftPanel from "./Login/LeftPanel";
import { PIVONY_LOGO_ON_LIGHT_BG } from "./Login/logos";
import { authWithGmail, logError, apiRequest, signout } from "../util/services";
import { trackLogin } from "../util/productAnalytics";
import {
  initAnonymousAnalytics,
  trackAnonymousAuthSubmit,
  trackAnonymousFormFieldFill,
  trackAnonymousOAuthClick,
} from "../util/anonymousAnalytics";
import { mergeAuthFieldSources, readInputValue } from "../util/authFieldAnalytics";
import {
  isFirebaseNetworkError,
  isFirebaseRecoverableAuthError,
  signInWithEmailAndPasswordWithNetworkRetry,
} from "../util/firebaseAuthRecovery";
import {
  fetchAuthChallengeStatus,
  verifyAuthChallengeToken,
  trackChallengeShown,
  shouldShowAuthCaptcha,
  isAuthCaptchaSatisfied,
  isAuthCaptchaExemptOrigin,
  isLoginCaptchaExemptEmail,
  buildPreAuthCheckPayload,
} from "../util/authChallenge";
import { USER } from "../util/constants.js";
import { isServiceUnavailableResult } from "../util/apiAvailability";
import { getPreAuthFailureNotice } from "./Login/preAuthFailureNotice";
import { accountReviewCode, accountReviewNotice, takeAccountReview } from "./Login/accountReview";
import AuthLanguageSwitch from "./AuthLanguageSwitch";
import {
  applyDocumentLanguage,
  normalizeAppLanguage,
  persistAppLanguage,
  resolvePreferredAppLanguage,
} from "../util/locale";

const TIMER = 120;
// After Firebase sign-in App.js gets the API token and leaves /login; past this the
// spinner stops so the user is never left waiting with no way forward.
const SESSION_START_TIMEOUT_MS = 30000;

const Login = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checkRecaptcha, setCheckRecaptcha] = useState(
    !config.RECAPTCHA_ENABLED
  );
  const [challengeRequired, setChallengeRequired] = useState(false);
  const [challengeStatus, setChallengeStatus] = useState(null);
  const [botBlocked, setBotBlocked] = useState(false);
  // Set when the account waits for approval or was rejected (here or after App.js signed it out).
  const [accountReview, setAccountReview] = useState(() => takeAccountReview());
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const [recaptchaVerifier, setRecaptchaVerifier] = useState();
  const [MFAView, setMFAView] = useState();
  const [verificationCode, setVerificationCode] = useState();
  const [verificationId, setVerificationId] = useState();
  const [timeLeft, setTimeLeft] = useState(0);
  const [rememberCheck, setRememberCheck] = useState(true);
  const passwordRef = useRef();
  const emailRef = useRef();
  const recaptchaRef = useRef();
  // Timer while waiting for App.js to finish the sign-in; null when not waiting.
  const sessionWaitRef = useRef(null);
  const onAuthSignedOutRef = useRef();
  const [showPassword, setShowPassword] = useState(false);
  const [failedLogin, setFailedLogin] = useState(
    sessionStorage.getItem("loginFailed")
  );
  const [loginErrors, setLoginErrors] = useState({
    email: "",
    password: "",
    captcha: "",
  });
  const [showRenewalForm, setShowRenewalForm] = useState(false);
  const [renewalSubmitted, setRenewalSubmitted] = useState(false);
  const [renewalSubmitting, setRenewalSubmitting] = useState(false);
  const [renewalForm] = Form.useForm();

  const getLoginAuthFields = () =>
    mergeAuthFieldSources(
      { email, password },
      {
        email: readInputValue(emailRef),
        password: readInputValue(passwordRef),
      },
    );

  useEffect(() => {
    const preferred = persistAppLanguage(
      resolvePreferredAppLanguage({
        search: window.location.search,
        stored: window.localStorage.getItem("userLang"),
        i18nLanguage: i18n.language,
      })
    );
    if (normalizeAppLanguage(i18n.language) !== preferred) {
      void i18n.changeLanguage(preferred);
    }
    applyDocumentLanguage(preferred);
    try {
      firebase.auth().languageCode = preferred;
    } catch {
      // ignore
    }
  }, [i18n]);

  useEffect(() => {
    window.localStorage.setItem("remember_me", rememberCheck);
  }, [rememberCheck]);

  useEffect(() => {
    if (isLoginCaptchaExemptEmail(email)) {
      setCheckRecaptcha(true);
    }
  }, [email]);

  const updateTime = () => {
    setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);
  };

  useEffect(() => {
    if (timeLeft > 0) updateTime();
  }, [timeLeft]);

  useEffect(() => {
    initAnonymousAnalytics();
    if (!config.RECAPTCHA_ENABLED || isAuthCaptchaExemptOrigin()) {
      setCheckRecaptcha(true);
      return;
    }
    // Checkbox is always required when reCAPTCHA is enabled.
    setCheckRecaptcha(false);
    if (!config.RISK_BASED_CAPTCHA) {
      return;
    }
    fetchAuthChallengeStatus().then((status) => {
      if (status?.blocked) {
        setBotBlocked(true);
        setChallengeStatus(status);
        setCheckRecaptcha(false);
        notification.error({
          message: t("auth.access_denied"),
          description: t("auth.access_denied_desc"),
        });
        return;
      }
      setChallengeStatus(status);
      const required = Boolean(status?.challenge_required);
      setChallengeRequired(required);
      trackChallengeShown(status);
    });
  }, []);

  // A ticked box is good for one attempt: a failed attempt (or a token that expired while
  // it ran) needs a new tick.
  const resetCaptcha = () => {
    if (!recaptchaRef.current) return;
    recaptchaRef.current.reset();
    setCheckRecaptcha(false);
    setLoginErrors((prev) => ({
      ...prev,
      captcha: t("login_page.captcha_again", "Please tick the verification box again."),
    }));
  };

  const stopWaitingForSession = () => {
    clearTimeout(sessionWaitRef.current);
    sessionWaitRef.current = null;
  };

  const failSessionStart = (description) => {
    stopWaitingForSession();
    setLoading(false);
    resetCaptcha();
    notification.error({ message: t("login_page.error", "Error"), description });
  };

  const waitForSession = () => {
    stopWaitingForSession();
    sessionWaitRef.current = setTimeout(
      () =>
        failSessionStart(
          t(
            "login_page.session_slow",
            "Sign-in is taking longer than usual. Check your connection and try again."
          )
        ),
      SESSION_START_TIMEOUT_MS
    );
  };

  // App.js signs the user out when it cannot finish the sign-in (no API token, account in review).
  onAuthSignedOutRef.current = () => {
    if (!sessionWaitRef.current) return;
    const review = takeAccountReview();
    if (review) {
      stopWaitingForSession();
      setLoading(false);
      resetCaptcha();
      setAccountReview(review);
      return;
    }
    failSessionStart(
      t("login_page.session_failed", "Sign-in could not be completed. Please try again.")
    );
  };

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((authUser) => {
      if (!authUser) onAuthSignedOutRef.current();
    });
    return () => {
      unsubscribe();
      stopWaitingForSession();
    };
  }, []);

  useEffect(() => {
    const verifier = new firebaseApp.auth.RecaptchaVerifier(
      "mfa-recaptcha-container",
      {
        size: "invisible",
      }
    );
    setRecaptchaVerifier(verifier);
  }, []);

  const signInUser = async (creds) => {
    if (botBlocked) return;
    const loginEmail = (creds?.email ?? email).trim();
    const loginPassword = creds?.password ?? password;
    setLoading(true);
    // if (
    //   !checkRecaptcha &&
    //   document.location.origin !== "http://localhost:3000" && document.location.origin !== "https://demo.pivony.com"
    // ) {
    //   notification.error({
    //     message: "Google Recaptcha",
    //     description: "You must verify with google recaptcha",
    //   });
    //   return;
    // }
    // if (!email || email.length === 0 || !password || password.length === 0) {
    //   notification.error({
    //     message: "Please Fill the required fields!",
    //   });
    //   return;
    // }
    var payload = buildPreAuthCheckPayload({ email: loginEmail, operation_type: "login" });
    // if (firebase.auth().currentUser) {
    //   firebase.auth().currentUser.getIdToken(true).then(() => { }).catch((err) => {
    //     window.location.href = window.location.origin + '/login'
    //   })
    // }
    const authCheckResult = await apiRequest(
      "users/pre_auth_check",
      "POST",
      payload,
      true,
      false,
      true
    );
    if (authCheckResult?.code === "ORGANIZATION_SUBSCRIPTION_EXPIRED") {
      setLoading(false);
      setShowRenewalForm(true);
      renewalForm.setFieldsValue({
        email: loginEmail,
        full_name: "",
        phone: "",
        message: "",
      });
      return;
    }

    const reviewCode = accountReviewCode(authCheckResult);
    if (reviewCode) {
      setLoading(false);
      setAccountReview(reviewCode);
      return;
    }

    if (
      !authCheckResult ||
      (authCheckResult || {}).error ||
      authCheckResult?.cancel === true
    ) {
      setLoading(false);
      trackAnonymousAuthSubmit("login", {
        success: false,
        method: "password",
        fields: getLoginAuthFields(),
      });
      const notice = getPreAuthFailureNotice(authCheckResult, t);
      if (notice.kind === "blocked") {
        setBotBlocked(true);
      }
      notification[notice.type]({
        message: notice.message,
        description: notice.description,
      });
      setLoading(false);
      resetCaptcha();
      try {
        apiRequest(
          "users/client_log",
          "POST",
          {
            email: loginEmail,
            error:
              notice.kind === "unavailable"
                ? "Pre-Auth Failed (service unavailable)"
                : "Pre-Auth Failed",
          },
          true
        );
      } catch (er) {
        console.log(er);
      }

      return;
    }

    if (authCheckResult.success) {
      try {
        const response = await signInWithEmailAndPasswordWithNetworkRetry(
          loginEmail,
          loginPassword
        );
        const res = response.user.toJSON();
        waitForSession();
        trackAnonymousAuthSubmit("login", {
          success: true,
          method: "password",
          fields: getLoginAuthFields(),
        });
        addLoginDate(res.uid);
      } catch (error) {
        if (error.code === "auth/multi-factor-auth-required") {
          const resolver = error.resolver;
          var phoneInfoOptions = {
            multiFactorHint: resolver.hints[0],
            session: resolver.session,
          };
          const phoneAuthProvider = new firebaseApp.auth.PhoneAuthProvider();
          phoneAuthProvider
            .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            .then((verificationId) => {
              setVerificationId(verificationId);
              setMFAView(resolver);
              setTimeLeft(TIMER);
              setLoading(false);
            })
            .catch((mfaError) => {
              setLoading(false);
              resetCaptcha();
              notification.error({
                message: t("login_page.error", "Error"),
                description: mfaError?.message,
              });
            });
          return;
        }

        const errorMessage = error.message;
        trackAnonymousAuthSubmit("login", {
          success: false,
          method: "password",
          fields: getLoginAuthFields(),
        });
        notification.error({
          message: t("login_page.error", "Error"),
          description: isFirebaseRecoverableAuthError(error)
            ? t(
                "login_page.network_error",
                "Connection to the sign-in service failed. If you are on a corporate network, try again or contact your IT team."
              )
            : errorMessage,
        });
        setLoading(false);
        resetCaptcha();
        try {
          apiRequest(
            "users/client_log",
            "POST",
            {
              email: loginEmail,
              error: errorMessage,
              message: isFirebaseNetworkError(error)
                ? "Login Failed (network, retry exhausted)"
                : "Login Failed",
            },
            true
          );
        } catch (er) {
          console.log(er);
        }
      }
    }
  };

  const onVerifyCode = async () => {
    try {
      setLoading(true);
      var cred = firebaseApp.auth.PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      var multiFactorAssertion = firebaseApp.auth.PhoneMultiFactorGenerator.assertion(
        cred
      );
      const userCredential = await MFAView.resolveSignIn(multiFactorAssertion);
      const res = userCredential.user.toJSON();
      addLoginDate(res.uid);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      notification.error({
        message: e.message,
      });
    }
  };

  const Resend = async () => {
    try {
      setTimeLeft(TIMER);
      setLoading(true);
      var cred = firebaseApp.auth.PhoneAuthProvider.credential(
        verificationId,
        "111111"
      );
      var multiFactorAssertion = firebaseApp.auth.PhoneMultiFactorGenerator.assertion(
        cred
      );
      await MFAView.resolveSignIn(multiFactorAssertion);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  function keyPressed(event) {
    if (event.key === "Enter") {
      handleValidation();
    }
  }
  const forgotPassword = () => {
    if (!email) {
      notification.error({
        message: "Email empty",
        description:
          "Enter your email in the email field and click forgot password",
      });
    } else {
      firebase
        .auth()
        .sendPasswordResetEmail(email)
        .then(function() {
          notification.success({
            message: t("login_page.email_sent", "Email sent"),
            description: t("login_page.reset_email_sent", "A reset email has been sent to {{email}}", { email }),
          });
          document.location.href =
            document.location.origin + `/reset-password?email=${email}`;
        })
        .catch((error) => {
          logError("Firebase Auth - sendPasswordResetEmail", error);
          notification.error({
            message: t("login_page.error_occurred", "Error occurred"),
            description: error.message,
          });
        });
    }
  };

  const showAuthCaptcha = shouldShowAuthCaptcha({
    challengeRequired,
    failedAuth: Boolean(failedLogin),
    email,
    context: "login",
  });

  const showRecaptcha = showAuthCaptcha;

  const verifyRecaptcha = async (token) => {
    if (!token) {
      setCheckRecaptcha(false);
      setLoginErrors((prev) => ({ ...prev, captcha: "" }));
      return;
    }

    setLoginErrors((prev) => ({ ...prev, captcha: "" }));
    // Google v2 checkbox already verified a human. Accept that for form submit.
    setCheckRecaptcha(true);

    if (!(config.RISK_BASED_CAPTCHA && config.RECAPTCHA_ENABLED)) {
      return;
    }
    // Server trust grant only when risk engine flagged the session or after failed login.
    if (!challengeRequired && !failedLogin) {
      return;
    }

    await verifyAuthChallengeToken(token, {
      reasons: challengeStatus?.reasons || [],
      bot_score: challengeStatus?.bot_score,
    });
  };

  const registerUID = async (uid, authUser, objUser) => {
    const idToken = await firebaseIdToken(authUser);
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    var requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ user_id: uid, email: objUser.email, id_token: idToken }),
    };

    await fetch(config.BASE_URL + "users/create", requestOptions)
      // JSON, not text: as text, a refusal had no .error and the account was set up anyway.
      .then((response) => response.json().catch(() => null))
      .then(async (result) => {
        if (!result || result.error) {
          authUser.delete();
          notification.error({
            message: t("login_page.error", "Error"),
            description: result?.error || "couldn't create user, please refresh and try again",
          });
        } else {
          firebase
            .database()
            .ref(`/users/${uid}`)
            .set(objUser)
            .then(() => {
              addLoginDate(uid);
            });
        }
      })
      .catch((error) => {
        authUser.delete();
        notification.error({
          message: t("login_page.error", "Error"),
          description: "couldn't create user, please refresh and try again",
        });
      });
  };

  const addLoginDate = async (uid) => {
    await firebase
      .database()
      .ref(`/users/${uid}`)
      .once("value", async (snapshot) => {
        let user = snapshot.val();
        let signInDates = user?.signInDates;
        if (!signInDates) {
          signInDates = {};
        }
        let now = new Date();
        signInDates[now.toString()] = true;

        await firebase
          .database()
          .ref(`/users/${uid}/signInDates`)
          .set(signInDates);
        trackLogin();
        // document.location.href = redirectURL
        //   ? redirectURL
        //   : document.location.origin + "/console/myDashboards";
      });
  };

  const signInWithGmail = async () => {
    trackAnonymousOAuthClick("google", "login");
    const result = await authWithGmail();
    if (result) {
      var payload = buildPreAuthCheckPayload({ email: result.email, operation_type: "login" });
      const authCheckResult = await apiRequest(
        "users/pre_auth_check",
        "POST",
        payload,
        true
      );
      if (authCheckResult?.code === "ORGANIZATION_SUBSCRIPTION_EXPIRED") {
        setLoading(false);
        setShowRenewalForm(true);
        renewalForm.setFieldsValue({
          email: result.email,
          full_name: result.displayName || "",
          phone: "",
          message: "",
        });
        signout(false);
        return;
      }

      const reviewCode = accountReviewCode(authCheckResult);
      if (reviewCode) {
        setLoading(false);
        setAccountReview(reviewCode);
        signout(false);
        return;
      }

      if (
        (authCheckResult || {}).error ||
        isServiceUnavailableResult(authCheckResult)
      ) {
        setLoading(false);
        const notice = getPreAuthFailureNotice(
          authCheckResult,
          t,
          authCheckResult.error
        );
        if (notice.kind === "blocked") {
          setBotBlocked(true);
        }
        notification[notice.type]({
          message: notice.message,
          description: notice.description,
        });
        signout(false);
        return;
      }

      firebase
        .database()
        .ref(`/users/${result.uid}`)
        .once("value", (snapshot) => {
          let user = snapshot.val();
          if (!user) {
            const objUser = {
              firstName: result.displayName,
              phone: result.phoneNumber,
              email: result.email,
              type: USER,
              emailVerified: true,
            };
            registerUID(result.uid, result, objUser);
          } else {
            // App.js onAuthStateChanged performs subscription-aware redirect from /login.
          }
        });
    }
  };

  const toggleShowHidePassword = () => {
    passwordRef?.current?.type === "password" && showPassword
      ? (passwordRef.current.type = "text")
      : (passwordRef.current.type = "password");
  };

  useEffect(() => {
    toggleShowHidePassword();
  }, [showPassword]);

  const removeMsg = () => {
    if (failedLogin) {
      setFailedLogin(sessionStorage.removeItem("loginFailed"));
    }
  };

  useEffect(() => {
    const clearFailedLoginOnLeave = () => {
      try {
        if (sessionStorage.getItem("loginFailed")) {
          sessionStorage.removeItem("loginFailed");
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("pagehide", clearFailedLoginOnLeave);
    return () => window.removeEventListener("pagehide", clearFailedLoginOnLeave);
  }, []);

  const handleValidation = () => {
    const fields = getLoginAuthFields();
    const emailValue = (fields.email || "").trim();
    const passwordValue = fields.password || "";
    setEmail(emailValue);
    setPassword(passwordValue);

    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const nextErrors = { email: "", password: "", captcha: "" };
    if (!emailValue) {
      nextErrors.email = "Enter a valid email address";
    } else if (!emailValue.match(validRegex)) {
      nextErrors.email = "Incorrect email format";
    }
    if (!passwordValue) {
      nextErrors.password = "Enter a valid password";
    }
    if (
      showAuthCaptcha &&
      !isAuthCaptchaSatisfied({
        challengeRequired: showAuthCaptcha,
        checkRecaptcha,
        email: emailValue,
        context: "login",
      })
    ) {
      nextErrors.captcha = "You must verify with google recaptcha";
    }
    setLoginErrors(nextErrors);
    if (nextErrors.email || nextErrors.password || nextErrors.captcha) {
      return;
    }
    signInUser({ email: emailValue, password: passwordValue });
  };

  const submitRenewalLead = async (values) => {
    setRenewalSubmitting(true);
    try {
      const result = await apiRequest(
        "users/organization/subscription_renewal_lead",
        "POST",
        {
          email: values.email,
          full_name: values.full_name,
          phone: values.phone || "",
          message: values.message || "",
        },
        true,
        false,
        true
      );
      if (result?.success) {
        setRenewalSubmitted(true);
        notification.success({
          message: t("login_page.renewal_request_received", "Request received"),
          description: result.success,
        });
        return;
      }
      notification.error({
        message: t("login_page.error", "Error"),
        description:
          result?.error ||
          t(
            "login_page.renewal_request_failed",
            "Could not send the request. Please try again."
          ),
      });
    } catch (err) {
      notification.error({
        message: t("login_page.error", "Error"),
        description: t(
          "login_page.renewal_request_failed",
          "Could not send the request. Please try again."
        ),
      });
    } finally {
      setRenewalSubmitting(false);
    }
  };

  const backToLogin = () => {
    setShowRenewalForm(false);
    setRenewalSubmitted(false);
    renewalForm.resetFields();
  };

  return (
    <div className={loginStyles.page}>
      <LeftPanel />
      <div className={loginStyles.rightPanel}>
        <AuthLanguageSwitch className={loginStyles.langSwitch} />
        <div className={loginStyles.formWrap}>
          <div className={loginStyles.mobileLogo}>
            <img
              src={PIVONY_LOGO_ON_LIGHT_BG}
              alt="Pivony"
              className={loginStyles.mobileLogoImg}
            />
          </div>
          <h2 className={loginStyles.pageTitle}>{t("login_page.welcome_back", "Welcome back.")}</h2>
          <p className={loginStyles.pageSubtitle}>
            {t("login_page.sign_in_subtitle", "Sign in to your Pivony account.")}
          </p>
          {MFAView ? (
            <div className={styles.verifyContainer}>
              <div className={styles.verifyTitle}>
                {t("login_page.mfa_sms_prompt", "Enter the code sent to your phone number via SMS")}
              </div>
              <div className={styles.customVerifyCode}>
                <ReactInputVerificationCode
                  onChange={(val) => setVerificationCode(val)}
                  value={verificationCode}
                  length={6}
                  placeholder=""
                  autoFocus="true"
                />
              </div>
              <div className={styles.resendText}>
                {timeLeft === 0 ? (
                  <>
                    {t("login_page.mfa_no_code", "Didn't receive a code?")}{" "}
                    <Button
                      className={styles.resendBtn}
                      onClick={() => Resend()}
                    >
                      {t("login_page.resend", "Resend")}
                    </Button>
                  </>
                ) : (
                  <i>{timeLeft} {t("login_page.seconds_left", "seconds left for verification")}</i>
                )}
              </div>
              <Button
                loading={loading}
                style={{ height: "auto" }}
                className={loginStyles.signInBtn}
                onClick={onVerifyCode}
              >
                {t("login_page.verify", "Verify")}
              </Button>
            </div>
          ) : showRenewalForm ? (
            <div className={styles.renewalFormContainer}>
              <p className={styles.renewalMessage}>
                {t(
                  "login_page.renewal_message",
                  "Your organization no longer has an active Pivony membership. Fill in the form below to arrange a conversation with Pivony sales through your organization's contact."
                )}
              </p>
              {renewalSubmitted ? (
                <div className={styles.renewalSuccess}>
                  <p>
                    {t(
                      "login_page.renewal_success",
                      "Request received. The Pivony sales team will contact you shortly."
                    )}
                  </p>
                  <Button
                    className={loginStyles.signInBtn}
                    onClick={backToLogin}
                  >
                    {t("login_page.renewal_back", "Back to sign in")}
                  </Button>
                </div>
              ) : (
                <Form
                  form={renewalForm}
                  layout="vertical"
                  onFinish={submitRenewalLead}
                  className={styles.renewalForm}
                >
                  <Form.Item
                    name="full_name"
                    label={t("login_page.renewal_full_name", "Full name")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "login_page.renewal_full_name_required",
                          "Enter your full name"
                        ),
                      },
                    ]}
                  >
                    <Input
                      className={styles.inputForm}
                      placeholder={t(
                        "login_page.renewal_full_name",
                        "Full name"
                      )}
                    />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label={t("login_page.renewal_email", "Email")}
                    rules={[
                      {
                        required: true,
                        message: t(
                          "login_page.renewal_email_required",
                          "Enter your email"
                        ),
                      },
                      {
                        type: "email",
                        message: t(
                          "login_page.renewal_email_invalid",
                          "Enter a valid email"
                        ),
                      },
                    ]}
                  >
                    <Input
                      className={styles.inputForm}
                      placeholder={t("login_page.renewal_email", "Email")}
                      readOnly
                    />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label={t("login_page.renewal_phone", "Phone")}
                  >
                    <Input
                      className={styles.inputForm}
                      placeholder={t(
                        "login_page.renewal_phone_placeholder",
                        "Phone (optional)"
                      )}
                    />
                  </Form.Item>
                  <Form.Item
                    name="message"
                    label={t("login_page.renewal_message_label", "Message")}
                  >
                    <Input.TextArea
                      className={`${styles.inputForm} ${styles.messageArea}`}
                      rows={4}
                      placeholder={t(
                        "login_page.renewal_message_placeholder",
                        "Add a note (optional)"
                      )}
                      maxLength={500}
                      showCount
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={renewalSubmitting}
                    className={loginStyles.signInBtn}
                  >
                    {t(
                      "login_page.renewal_submit",
                      "Request a conversation"
                    )}
                  </Button>
                  <Button
                    type="link"
                    onClick={backToLogin}
                    className={styles.renewalBackLink}
                  >
                    {t("login_page.renewal_back", "Back to sign in")}
                  </Button>
                </Form>
              )}
            </div>
          ) : (
            <>
              {accountReview ? (
                <Alert
                  className={loginStyles.accountReview}
                  type={accountReviewNotice(accountReview, t).type}
                  showIcon
                  closable
                  onClose={() => setAccountReview(null)}
                  message={accountReviewNotice(accountReview, t).title}
                  description={accountReviewNotice(accountReview, t).description}
                />
              ) : null}
              {botBlocked ? (
                <div className={styles.loginFailedMsg}>
                  <div className={styles.msg}>
                    <span>Access denied.</span>
                    <span>Automated browsers cannot sign in. Please use a standard browser.</span>
                  </div>
                </div>
              ) : null}
              {failedLogin && (
                <div className={styles.loginFailedMsg}>
                  <div className={styles.msg}>
                    <span>You have been logged out.</span>
                    <span>Please login back to continue.</span>
                  </div>
                  <img src="/assets/x.svg" onClick={removeMsg} alt="" />
                </div>
              )}
              <div>
                <label className={loginStyles.fieldLabel} htmlFor="login-email">
                  Business email
                </label>
                <div className={loginStyles.emailFieldWrap}>
                  <input
                    id="login-email"
                    ref={emailRef}
                    className={loginStyles.inputField}
                    onKeyPress={keyPressed}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={(e) => {
                      trackAnonymousFormFieldFill(
                        "email",
                        "login",
                        Boolean(e.target.value),
                        e.target.value,
                      );
                    }}
                    value={email}
                    type="email"
                    placeholder="name@company.com"
                    autoComplete="email"
                  />
                  {loginErrors.email ? (
                    <p className={loginStyles.loginErrorMsg}>
                      {loginErrors.email}
                    </p>
                  ) : null}
                </div>
                <div className={loginStyles.passwordRow}>
                  <label className={loginStyles.fieldLabel} htmlFor="login-password">
                    Password
                  </label>
                  <a className={loginStyles.forgotLink} onClick={forgotPassword}>
                    Forgot?
                  </a>
                </div>
                <div className={loginStyles.passwordFieldWrap}>
                  <input
                    id="login-password"
                    ref={passwordRef}
                    className={loginStyles.inputField}
                    onKeyPress={keyPressed}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={(e) => {
                      trackAnonymousFormFieldFill(
                        "password",
                        "login",
                        Boolean(e.target.value),
                        e.target.value,
                      );
                    }}
                    value={password}
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  {showPassword ? (
                    <img
                      src="/assets/open_eye.svg"
                      className={styles.showHidePassword}
                      alt=""
                      onClick={() => setShowPassword(false)}
                    />
                  ) : (
                    <img
                      src="/assets/close_eye.svg"
                      className={styles.showHidePassword}
                      alt=""
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                  {loginErrors.password ? (
                    <p className={loginStyles.loginErrorMsg}>
                      {loginErrors.password}
                    </p>
                  ) : null}
                </div>
                <div className={loginStyles.rememberRow}>
                  <div className={styles.rememberMeBlock}>
                    <input
                      type="checkbox"
                      checked={rememberCheck}
                      onChange={(e) => setRememberCheck(e.target.checked)}
                      className={styles.rememberMeBtn}
                    />
                    <span className={styles.rememberMeLabel}>
                      Remember me
                    </span>
                  </div>
                </div>
                {showRecaptcha && (
                  <div className={loginStyles.captchaWrap}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      style={{ display: "inline-block" }}
                      onChange={verifyRecaptcha}
                      onExpired={() => verifyRecaptcha(null)}
                      sitekey={config.RECAPTCHA_SITE_PUBLIC_KEY}
                    />
                    {loginErrors.captcha ? (
                      <p className={loginStyles.loginErrorMsg}>
                        {loginErrors.captcha}
                      </p>
                    ) : null}
                  </div>
                )}
                <div id="mfa-recaptcha-container" />
                <Button
                  id="login-sign-in"
                  style={{ height: "auto" }}
                  type="primary"
                  loading={loading}
                  disabled={botBlocked}
                  className={loginStyles.signInBtn}
                  onClick={handleValidation}
                >
                  Sign in
                </Button>
              </div>
              <p className={loginStyles.signupHint}>
                {t("login_page.no_account", "Don't have an account?")}{" "}
                <Link to="/signup">
                  {t("login_page.create_free_account", "Create a free account")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
